import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';
import { Download, ArrowLeft, Database, Loader2, FileDown, Archive } from 'lucide-react';

function downloadJSON(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
}

export default function DataExport() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [fullBusy, setFullBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiGet('/settings/backup/collections');
      setGroups(r.groups || []);
      setTotalRows(r.total_rows || 0);
    } catch (err) {
      toast.error('Failed to load collection list');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const exportOne = async (collection) => {
    setBusy(collection);
    try {
      const r = await apiGet(`/settings/backup/export/${collection}?limit=50000`);
      downloadJSON(`${collection}-${new Date().toISOString().slice(0, 10)}.json`, r);
      toast.success(`Exported ${r.count} rows from "${collection}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
    setBusy(null);
  };

  const exportFull = async () => {
    if (!window.confirm(`Download a FULL backup of all collections (~${totalRows.toLocaleString()} rows total)? This may take a minute.`)) return;
    setFullBusy(true);
    try {
      const r = await apiGet('/settings/backup/full');
      const fname = `ellora-erp-full-backup-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      downloadJSON(fname, r);
      toast.success(`Full backup downloaded — ${r.total_rows.toLocaleString()} rows`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Full backup failed');
    }
    setFullBusy(false);
  };

  return (
    <div className="p-6 space-y-6" data-testid="data-export-page">
      <button onClick={() => navigate('/app/settings')} className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-display font-bold text-slate-500 hover:text-[#1F7A6E]">
        <ArrowLeft size={14} /> Back to Settings
      </button>

      <PageHeader
        title="Data Export & Backup"
        subtitle="Download any module's MongoDB collection as JSON. Use these files for off-site backup, migration, or to import into a local development copy."
        icon={Database}
      />

      <div className="bg-gradient-to-r from-teal-50 to-violet-50 border border-teal-200 rounded-sm p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-display font-bold text-base text-[#0F172A]">Full Backup</div>
          <div className="text-xs text-slate-600 mt-0.5">
            One JSON file with every collection — {loading ? '…' : <b>{totalRows.toLocaleString()} rows total</b>}. Recommended for off-site backups.
          </div>
        </div>
        <button
          onClick={exportFull}
          disabled={fullBusy || loading}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50"
          data-testid="export-full-btn"
        >
          {fullBusy ? <Loader2 size={14} className="animate-spin" /> : <Archive size={14} />} {fullBusy ? 'Bundling…' : 'Download Full Backup'}
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading collections…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {groups.map(g => (
            <div key={g.group} className="bg-white border border-slate-200 rounded-sm" data-testid={`group-${g.group.replace(/\s/g, '-').toLowerCase()}`}>
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-display font-bold text-sm uppercase tracking-[0.12em] text-[#1F7A6E]">{g.group}</h3>
                <span className="text-xs text-slate-500">{g.total.toLocaleString()} rows</span>
              </div>
              <div className="divide-y divide-slate-100">
                {g.items.map(item => (
                  <div key={item.collection} className="px-5 py-2 flex items-center justify-between hover:bg-slate-50" data-testid={`row-${item.collection}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs text-[#0F172A] truncate">{item.collection}</div>
                      <div className="text-[10px] text-slate-500">{item.count.toLocaleString()} rows</div>
                    </div>
                    <button
                      onClick={() => exportOne(item.collection)}
                      disabled={busy === item.collection || item.count === 0}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-display font-bold px-2.5 py-1 border border-slate-200 hover:border-[#1F7A6E] hover:text-[#1F7A6E] rounded-sm disabled:opacity-40"
                      title={item.count === 0 ? 'Empty — nothing to export' : 'Download as JSON'}
                      data-testid={`export-${item.collection}`}
                    >
                      {busy === item.collection ? <Loader2 size={11} className="animate-spin" /> : <FileDown size={11} />}
                      {busy === item.collection ? 'Exporting…' : 'JSON'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-xs text-slate-500 border-t border-slate-200 pt-4">
        <p><b>How to use these files</b></p>
        <ul className="list-disc pl-5 mt-1 space-y-0.5">
          <li>Each JSON file is a MongoDB-compatible dump and can be imported back via <code className="bg-slate-100 px-1 rounded-sm">POST /api/settings/backup/import</code> (admin only).</li>
          <li>To import locally: use <code className="bg-slate-100 px-1 rounded-sm">mongoimport --jsonArray --collection &lt;name&gt; --file data.json</code> after extracting the <code className="bg-slate-100 px-1 rounded-sm">data</code> array from the JSON.</li>
          <li>Full backups include every module — keep them encrypted and off-site for compliance.</li>
        </ul>
      </div>
    </div>
  );
}
